import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import tinycolor from 'tinycolor2';

import { useWindowSize, useLoading } from '../../contexts/SiteContext';

import styles from '../../../styles/partials/background-text.module.scss';

const propTypes = {
    text: PropTypes.string,
    scrolled: PropTypes.bool,
    maxHeight: PropTypes.number,
    colors: PropTypes.shape({
        primary: PropTypes.string,
        secondary: PropTypes.string,
    }),
    className: PropTypes.string,
};

const defaultProps = {
    text: null,
    scrolled: false,
    maxHeight: null,
    colors: null,
    className: null,
};

const BackgroundText = ({ text: backgroundText, scrolled, maxHeight, colors, className }) => {
    const { width } = useWindowSize();
    const [animate, setAnimate] = useState(false);
    const { loading } = useLoading();

    const fontSize = Math.max(Math.floor(width / 30), 30);

    useEffect(() => {
        let timeout;
        if (!scrolled) {
            timeout = setTimeout(() => setAnimate(true), loading ? 500 : 50);
        } else {
            timeout = setTimeout(() => setAnimate(false), 50);
        }
        return () => {
            if (timeout) {
                clearTimeout(timeout);
            }
        };
    }, [scrolled]);

    const style = useMemo(() => {
        if (maxHeight !== null) {
            const modulo = maxHeight % fontSize;
            const height = maxHeight - modulo - fontSize * 0.2;
            return {
                maxHeight: `${height}px`,
                overflow: 'hidden',
                fontSize: `${fontSize}px`,
            };
        }
        return null;
    }, [maxHeight, fontSize]);

    const colorStyles = useMemo(() => {
        const { primary = null, secondary = null } = colors || {};
        if (primary !== null && secondary === null)
            return { color: tinycolor(primary).lighten(10).toString() };
        if (primary !== null && secondary !== null) {
            const top = tinycolor(secondary).lighten(10).toString();
            const middle = tinycolor(primary).lighten(20).toString();
            const center = tinycolor(primary).lighten(30).toString();
            const bottom = tinycolor(secondary).lighten(10).toString();
            return {
                backgroundImage: `linear-gradient(to bottom, ${top}, ${middle} 30%, ${center} 50%, ${middle} 70%, ${bottom})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitBoxDecorationBreak: 'clone',
                // display: 'block',
                color: 'rgba(255,255,255,0.3)',
                // color: 'transparent',
            };
        }
        return null;
    }, [colors]);

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.animate]: animate,
                    [className]: className !== null,
                },
            ])}
            style={{ ...style, ...colorStyles }}
        >
            <div className={styles.backgroundText}>
                {backgroundText.split(' ').map((l, idx) => (
                    <div
                        key={`letter-${idx + 1}`}
                        className={classNames([
                            styles.letterContainer,
                            {
                                // [styles.animate]: animate,
                            },
                        ])}
                        style={{
                            transitionDelay: `${
                                window.innerWidth < 768 ? idx * 0.01 : idx * 0.005
                            }s`,
                        }}
                    >
                        <div className={styles.letter}>{l}</div>
                    </div>
                ))}
            </div>
        </div>
    );
};

BackgroundText.propTypes = propTypes;
BackgroundText.defaultProps = defaultProps;

export default BackgroundText;
